import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import axios from "axios";
import { Dialog, Button, Dropdown, TextInput, Image } from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
} from "@utils/extra-style";
import UserInfo from "@utils/UserInfo";
import {
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  OptionsWrapper,
  FlexRow,
  SectionContainer,
  GridRow,
} from "./AddEditBankAccount.styled";
import closeIcon from "@icons/ic-close-white.svg";

const AddEditBankAccount = (props) => {
  const {
    t,
    id,
    isEditMode,
    title,
    onClose,
    updateRefeshStatus,
    selectedAccount,
  } = props;

  const [isAccountCreating, setIsAccountCreating] = useState(false);
  const [createAccountAPIError, setCreateAccountAPIError] = useState("");

  const generalParams = useGetGeneralParameters();
  const userInfo = UserInfo.getUser();

  const { countryList } = generalParams;
  const sortedCountryList = [...countryList]
    .sort((a, b) => a.label.localeCompare(b.label))
    .filter((country) => country.platform === 1);
  const [formData, setFormData] = useState({
    label: {
      required: true,
      value: selectedAccount?.label ?? "",
      error: "",
    },
    bankName: {
      required: true,
      value: selectedAccount?.bankName ?? "",
      error: "",
    },
    rib: {
      required: true,
      value: selectedAccount?.rib ?? "",
      error: "",
    },
    iban: {
      required: true,
      value: selectedAccount?.iban ?? "",
      error: "",
    },
    bic: {
      required: true,
      value: selectedAccount?.bic ?? "",
      error: "",
    },
    country: {
      required: true,
      value: selectedAccount?.country?.id ?? "",
      error: "",
    },
    isActive: {
      required: true,
      value: selectedAccount?.isActive ?? true,
      error: "",
    },
  });

  const onCountryChange = (e) => {
    setFormData({
      ...formData,
      country: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };
  const onLabelChange = (e) => {
    setFormData({
      ...formData,
      label: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onBankNameChange = (e) => {
    setFormData({
      ...formData,
      bankName: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onRibChange = (e) => {
    setFormData({
      ...formData,
      rib: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onIbanChange = (e) => {
    setFormData({
      ...formData,
      iban: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onBicChange = (e) => {
    setFormData({
      ...formData,
      bic: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.country.value) {
      isFormValid = false;
      data.country.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.label.value) {
      isFormValid = false;
      data.label.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.bankName.value) {
      isFormValid = false;
      data.bankName.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.iban.value) {
      isFormValid = false;
      data.iban.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.bic.value) {
      isFormValid = false;
      data.bic.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!isFormValid) {
      setFormData({ ...data });
    }

    return isFormValid;
  };

  const getFormData = () => {
    const data = {
      id: isEditMode ? selectedAccount.id : "",
      label: formData.label.value,
      bankName: formData.bankName.value,
      rib: formData.rib.value,
      iban: formData.iban.value,
      bic: formData.bic.value,
      isActive: formData.isActive.value,
      country: JSON.stringify({
        id: formData.country.value,
      }),
      createdBy: JSON.stringify({ id: userInfo.id }),
      updatedBy: JSON.stringify({ id: userInfo.id }),
    };
    return data;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      setIsAccountCreating(true);
      setCreateAccountAPIError("");
      // Create solution
      if (!id && !isEditMode) {
        axios
          .post("/general/bankAccounts", getFormData())
          .then(() => {
            setIsAccountCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsAccountCreating(false);
            setCreateAccountAPIError(error.response?.data?.message ?? "");
          });
      }
      if (id && isEditMode) {
        axios
          .put(`/general/bankAccounts/${id}`, getFormData())
          .then(() => {
            setIsAccountCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsAccountCreating(false);
            setCreateAccountAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };

  return (
    <Dialog>
      <div>
        <HeaderWrapper>
          <Title>
            {title} {id ? "#" + id : ""}
          </Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close Product price popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        <FormBody>
          <OptionsWrapper>
            <FlexRow>
              <SectionContainer>
                <div>
                  <GridRow column="1fr 1fr">
                    <Dropdown
                      id="product-country"
                      label={t("COUNTRY")}
                      name="country"
                      value={formData.country.value}
                      onChange={onCountryChange}
                      defaultOption={t("COUNTRY")}
                      options={sortedCountryList}
                      labelStyles={
                        formData.country.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.country.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      required
                      error={formData.country.error}
                    />

                    <TextInput
                      id="label"
                      type="text"
                      label={t("BANK_ACCOUNT_LABEL")}
                      isFloat
                      required
                      value={String(formData.label.value)}
                      onChange={(e) => onLabelChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.label.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.label.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.label.error}
                      placeholder={t("BANK_ACCOUNT_LABEL")}
                    />
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <TextInput
                      id="bank_name"
                      type="text"
                      label={t("BANK_ACCOUNT_BANK_NAME")}
                      isFloat
                      required
                      value={String(formData.bankName.value)}
                      onChange={(e) => onBankNameChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.bankName.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.bankName.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.bankName.error}
                      placeholder={t("BANK_ACCOUNT_BANK_NAME")}
                    />
                    <TextInput
                      id="bank_account_rib"
                      type="text"
                      label={t("BANK_ACCOUNT_RIB")}
                      isFloat
                      value={String(formData.rib.value)}
                      onChange={(e) => onRibChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.rib.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.rib.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.rib.error}
                      placeholder={t("BANK_ACCOUNT_RIB")}
                    />
                  </GridRow>

                  <GridRow column="1fr 1fr">
                    <TextInput
                      id="bank_iban"
                      type="text"
                      label={t("BANK_ACCOUNT_IBAN")}
                      isFloat
                      required
                      value={String(formData.iban.value)}
                      onChange={(e) => onIbanChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.iban.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.iban.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.iban.error}
                      placeholder={t("BANK_ACCOUNT_IBAN")}
                    />
                    <TextInput
                      id="bank_account_bic"
                      type="text"
                      label={t("BANK_ACCOUNT_BIC")}
                      isFloat
                      required
                      value={String(formData.bic.value)}
                      onChange={(e) => onBicChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.bic.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.bic.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.bic.error}
                      placeholder={t("BANK_ACCOUNT_BIC")}
                    />
                  </GridRow>
                </div>
              </SectionContainer>
            </FlexRow>
          </OptionsWrapper>
        </FormBody>

        <ButtonWrapper>
          {createAccountAPIError && <div>{createAccountAPIError}</div>}
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isAccountCreating && <ClipLoader size={15} color="#fff" />}
              {!isAccountCreating && t("SUBMIT")}
            </>
          </Button>

          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

AddEditBankAccount.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  title: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  updateRefeshStatus: PropTypes.func.isRequired,
};

AddEditBankAccount.defaultProps = {
  id: null,
  isEditMode: false,
};
export default withTranslation()(AddEditBankAccount);
